import { ChatCenteredTextIcon } from '@phosphor-icons/react'
import { Handle, type NodeProps, Position, useNodeConnections, useNodeId } from '@xyflow/react'
import { cn } from 'app/lib/utils'
import { formatDuration } from 'app/lib/workflows/format'
import { FIXED_REPLY_EXITS } from 'app/lib/workflows/replyKeywords'
import type { WaitForKeywordFlowNode } from 'app/types/workflows'
import { AppendButton } from './AppendButton'
import { NodeShell } from './NodeShell'

const HANDLE = `!h-2.5 !w-2.5 !border-2 !border-(--surface) !left-(--outcome-left)`

// The card grows by this much per exit, so option names stay readable however many there are.
// Three exits come to the standard card width.
const REM_PER_EXIT = 5

type Exit = { id: string; label: string; title?: string; tone: `option` | `fixed` | `stranded` }

const EXIT_TEXT: Record<Exit[`tone`], string> = {
  option: `text-default`,
  fixed: `text-minimal`,
  stranded: `text-red-600`,
}

/** Forks on what the contact replies to an upstream Send SMS: one exit per reply option, then Other
 *  reply and No reply. Coupled to a send, like Wait for reply (enforced via the shared issues map;
 *  see NodeShell). */
export function WaitForKeywordNode({ data, selected }: NodeProps<WaitForKeywordFlowNode>) {
  const nodeId = useNodeId()
  const connections = useNodeConnections({ handleType: `source` })
  const current = new Set<string>([...data.options.map((option) => option.id), ...FIXED_REPLY_EXITS])
  // Steps still hang off an option that was removed. Its exit stays drawn so the issue that blocks
  // Activate points at something on the card.
  const stranded = [
    ...new Set(
      connections.flatMap(({ sourceHandle }) => (sourceHandle && !current.has(sourceHandle) ? [sourceHandle] : [])),
    ),
  ]
  const listedExits: Exit[] = [
    // A new option starts unnamed, so its exit falls back to its position until it gets a name.
    ...data.options.map(
      (option, index): Exit => ({
        id: option.id,
        label: (option.label.trim() || `Option ${index + 1}`).toUpperCase(),
        title: option.label.trim() || undefined,
        tone: `option`,
      }),
    ),
    ...stranded.map((id): Exit => ({ id, label: `REMOVED OPTION`, tone: `stranded` })),
    { id: `other_reply`, label: `OTHER REPLY`, tone: `fixed` },
    { id: `no_reply`, label: `NO REPLY`, tone: `fixed` },
  ]
  // Spread evenly: each exit sits in the middle of its equal share of the card.
  const exits = listedExits.map((exit, index) => ({ ...exit, left: `${((index + 0.5) / listedExits.length) * 100}%` }))

  const names = data.options
    .filter((option) => option.keywords.length && option.label.trim())
    .map((option) => option.label)
  const last = names.pop()
  const listed = last && (names.length ? `${names.join(`, `)} or ${last}` : last)
  const deadline = data.within ? `, within ${formatDuration(data.within, data.unit ?? `hours`)}` : ``

  return (
    <NodeShell
      icon={ChatCenteredTextIcon}
      accent="violet"
      kind="Wait for a keyword"
      label={data.label}
      description={listed ? `${listed}${deadline}` : `No options yet`}
      selected={selected}
      hasSource={false}
      width={`${Math.max(3, exits.length) * REM_PER_EXIT}rem`}
    >
      <div className="flex pb-2 text-caption-sm font-semibold">
        {exits.map((exit) => (
          <span
            key={exit.id}
            title={exit.title}
            className={cn(`min-w-0 flex-1 basis-0 truncate text-center`, EXIT_TEXT[exit.tone])}
          >
            {exit.label}
          </span>
        ))}
      </div>
      {exits.map((exit) => (
        <Handle
          key={exit.id}
          id={exit.id}
          type="source"
          position={Position.Bottom}
          style={{ '--outcome-left': exit.left } as React.CSSProperties}
          className={cn(HANDLE, exit.tone === `fixed` ? `!bg-inverse/40` : `!bg-inverse`)}
        />
      ))}
      {nodeId != null &&
        exits.map((exit) => <OutcomeAppend key={exit.id} nodeId={nodeId} handle={exit.id} left={exit.left} />)}
    </NodeShell>
  )
}

/** Append "+" for one outcome handle, shown only while that handle is unconnected. */
function OutcomeAppend({ nodeId, handle, left }: { nodeId: string; handle: string; left: string }) {
  const open = useNodeConnections({ handleType: `source`, handleId: handle }).length === 0
  if (!open) return null
  return (
    <AppendButton
      nodeId={nodeId}
      sourceHandle={handle}
      className="-bottom-8 left-(--outcome-left) -translate-x-1/2"
      style={{ '--outcome-left': left } as React.CSSProperties}
    />
  )
}
